import React from "react";
import { createPortal } from "react-dom";
import { normalizeMentionItem, type MentionSuggestionItem } from "@seavien/commons";
import { Input } from "@/components/ui/input";
import {
    MIN_REMOTE_USER_SUGGESTION_QUERY_LENGTH,
    type UserSuggestionContext,
    userSuggestionService,
    USER_SUGGESTION_REQUEST_DEBOUNCE_MS,
} from "@/main/common/UserSuggestionService";
import MentionSuggestionPopup from "@/main/react/editor/mention/MentionSuggestionPopup";
import type { Localizer } from "@/main/react/Types";

const MAX_USERNAME_SUGGESTIONS = 5;

function normalizeSuggestionQuery(value: string): string {
    return String(value ?? "").trim().replace(/^@+/, "");
}

type UsernameSuggestionFieldProps = Omit<React.ComponentProps<typeof Input>, "onChange" | "value"> & {
    localizer: Localizer;
    value: string;
    onChange: (value: string) => void;
    suggestionContext?: UserSuggestionContext;
    onSuggestionSelected?: (item: MentionSuggestionItem) => void;
};

const UsernameSuggestionField = React.forwardRef<HTMLInputElement, UsernameSuggestionFieldProps>(function UsernameSuggestionField({
    localizer,
    value,
    onChange,
    suggestionContext = {},
    onSuggestionSelected,
    disabled,
    readOnly,
    onFocus,
    onBlur,
    onKeyDown,
    className,
    ...inputProps
}, forwardedRef) {
    const [focused, setFocused] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [phase, setPhase] = React.useState<"idle" | "loading" | "ready" | "error">("idle");
    const [suggestions, setSuggestions] = React.useState<MentionSuggestionItem[]>([]);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = React.useState(0);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const popupRef = React.useRef<HTMLDivElement | null>(null);
    const [popupPosition, setPopupPosition] = React.useState<{ top: number; left: number; width: number } | null>(null);
    const requestIdRef = React.useRef(0);
    const suppressQueryRef = React.useRef<string | null>(null);
    const stableSuggestionContext = React.useMemo<UserSuggestionContext>(() => ({
        discussionPublicId: suggestionContext.discussionPublicId,
        relatedUsers: suggestionContext.relatedUsers ?? [],
    }), [suggestionContext.discussionPublicId, suggestionContext.relatedUsers]);

    const setRefs = React.useCallback((node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof forwardedRef === "function") {
            forwardedRef(node);
            return;
        }
        if (forwardedRef) {
            forwardedRef.current = node;
        }
    }, [forwardedRef]);

    React.useEffect(() => {
        if (disabled || readOnly) {
            return;
        }

        userSuggestionService.ensurePreloaded(stableSuggestionContext);
    }, [disabled, readOnly, stableSuggestionContext]);

    React.useEffect(() => {
        if (!open) {
            return;
        }

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node | null;
            if (!target || containerRef.current?.contains(target) || popupRef.current?.contains(target)) {
                return;
            }

            setFocused(false);
            setOpen(false);
        };

        document.addEventListener("mousedown", handlePointerDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
        };
    }, [open]);

    React.useEffect(() => {
        if (!focused || disabled || readOnly) {
            setOpen(false);
            setPhase("idle");
            return;
        }

        const normalizedQuery = normalizeSuggestionQuery(value);
        if (!normalizedQuery) {
            setSuggestions([]);
            setSelectedSuggestionIndex(0);
            setOpen(false);
            setPhase("idle");
            suppressQueryRef.current = null;
            return;
        }

        const suppressedQuery = suppressQueryRef.current;
        if (suppressedQuery && suppressedQuery === normalizedQuery.toLowerCase()) {
            suppressQueryRef.current = null;
            setOpen(false);
            setPhase("ready");
            return;
        }

        const snapshot = userSuggestionService.getSuggestionSnapshot(normalizedQuery, MAX_USERNAME_SUGGESTIONS, stableSuggestionContext);
        setSuggestions(snapshot.items);
        setSelectedSuggestionIndex(0);

        if (!snapshot.shouldFetchRemote) {
            setPhase("ready");
            setOpen(snapshot.items.length > 0);
            return;
        }

        const requestId = ++requestIdRef.current;
        setPhase("loading");
        setOpen(true);

        const timeoutId = window.setTimeout(async () => {
            try {
                const items = await userSuggestionService.fetchRemoteSuggestions(normalizedQuery, MAX_USERNAME_SUGGESTIONS, stableSuggestionContext);
                if (requestIdRef.current !== requestId) {
                    return;
                }

                setSuggestions(items);
                setSelectedSuggestionIndex(0);
                setPhase("ready");
                setOpen(items.length > 0 || normalizedQuery.length >= MIN_REMOTE_USER_SUGGESTION_QUERY_LENGTH);
            } catch {
                if (requestIdRef.current !== requestId) {
                    return;
                }

                setSuggestions([]);
                setSelectedSuggestionIndex(0);
                setPhase("error");
                setOpen(true);
            }
        }, USER_SUGGESTION_REQUEST_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [disabled, focused, readOnly, stableSuggestionContext, value]);

    const handleSelectSuggestion = React.useCallback((item: MentionSuggestionItem) => {
        const normalizedItem = normalizeMentionItem(item);
        const username = normalizeSuggestionQuery(normalizedItem.username ?? normalizedItem.label ?? normalizedItem.id);
        if (!username) {
            return;
        }

        userSuggestionService.rememberSelectedUser(normalizedItem);
        suppressQueryRef.current = username.toLowerCase();
        onChange(username);
        onSuggestionSelected?.(normalizedItem);
        setOpen(false);
        setPhase("idle");
        inputRef.current?.focus();
    }, [onChange, onSuggestionSelected]);

    const normalizedQuery = normalizeSuggestionQuery(value);
    const showNoResults = phase === "ready"
        && suggestions.length === 0
        && normalizedQuery.length >= MIN_REMOTE_USER_SUGGESTION_QUERY_LENGTH;
    const statusMessage = phase === "error"
        ? localizer("user.suggestions.load_failed")
        : showNoResults
            ? localizer("user.suggestions.none_found")
            : null;
    const showDropdown = open && (phase === "loading" || suggestions.length > 0 || Boolean(statusMessage));

    // The dropdown is portalled to document.body so it escapes any scrolling/overflow-hidden
    // ancestor (e.g. a SimpleDialog body); position it as a fixed layer anchored to the input.
    React.useLayoutEffect(() => {
        if (!showDropdown) {
            setPopupPosition(null);
            return;
        }

        const reposition = () => {
            const input = inputRef.current;
            if (!input) {
                return;
            }

            const rect = input.getBoundingClientRect();
            const gap = 8;
            const popupHeight = popupRef.current?.offsetHeight ?? 0;
            const spaceBelow = window.innerHeight - rect.bottom;
            const placeAbove = popupHeight > 0 && spaceBelow < popupHeight + gap && rect.top > spaceBelow;
            const top = placeAbove ? Math.max(gap, rect.top - gap - popupHeight) : rect.bottom + gap;
            // Same position, same state: a scroll that did not move the input must not re-render.
            setPopupPosition((current) => (
                current && current.top === top && current.left === rect.left && current.width === rect.width
                    ? current
                    : { top, left: rect.left, width: rect.width }
            ));
        };

        // At most one layout read per frame: on a phone signup form, scrolling with the keyboard up fires
        // many capture-phase scroll events a frame (the `HeaderSearchSuggestions` pattern).
        let frame: number | null = null;
        const scheduleReposition = () => {
            if (frame !== null) {
                return;
            }
            frame = window.requestAnimationFrame(() => {
                frame = null;
                reposition();
            });
        };

        reposition();
        window.addEventListener("scroll", scheduleReposition, true);
        window.addEventListener("resize", scheduleReposition);
        return () => {
            if (frame !== null) {
                window.cancelAnimationFrame(frame);
            }
            window.removeEventListener("scroll", scheduleReposition, true);
            window.removeEventListener("resize", scheduleReposition);
        };
    }, [showDropdown, suggestions, phase, statusMessage]);

    return (
        <div ref={containerRef} className="relative">
            <Input
                {...inputProps}
                ref={setRefs}
                value={value}
                disabled={disabled}
                readOnly={readOnly}
                className={className}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                onFocus={(event) => {
                    setFocused(true);
                    onFocus?.(event);
                }}
                onBlur={(event) => {
                    onBlur?.(event);
                }}
                onChange={(event) => {
                    suppressQueryRef.current = null;
                    onChange(event.target.value);
                }}
                onKeyDown={(event) => {
                    if (showDropdown && suggestions.length > 0) {
                        if (event.key === "ArrowDown") {
                            event.preventDefault();
                            setSelectedSuggestionIndex((previous) => (previous + 1) % suggestions.length);
                            return;
                        }

                        if (event.key === "ArrowUp") {
                            event.preventDefault();
                            setSelectedSuggestionIndex((previous) => (previous + suggestions.length - 1) % suggestions.length);
                            return;
                        }

                        if (event.key === "Enter" || event.key === "Tab") {
                            const selectedSuggestion = suggestions[selectedSuggestionIndex] ?? suggestions[0];
                            if (selectedSuggestion) {
                                event.preventDefault();
                                handleSelectSuggestion(selectedSuggestion);
                                return;
                            }
                        }
                    }

                    if (event.key === "Escape" && showDropdown) {
                        event.preventDefault();
                        setOpen(false);
                        setFocused(false);
                        setPhase("idle");
                        return;
                    }

                    onKeyDown?.(event);
                }}
            />

            {showDropdown && typeof document !== "undefined"
                ? createPortal(
                    <div
                        ref={popupRef}
                        data-username-suggestion-popup=""
                        className="z-layer-top"
                        style={{
                            position: "fixed",
                            top: popupPosition?.top ?? -9999,
                            left: popupPosition?.left ?? -9999,
                            width: popupPosition?.width,
                            visibility: popupPosition ? "visible" : "hidden",
                            // Re-enable interaction when hosted inside a modal dialog: Radix disables
                            // pointer events on everything outside its content, and this popup is
                            // portalled to <body>, so it would otherwise be un-clickable.
                            pointerEvents: "auto",
                        }}
                        // Keep pointer interactions from reaching the dialog's outside-dismiss
                        // listener (selection happens on the inner button's mousedown, which fires
                        // first), so picking a suggestion never closes the surrounding dialog.
                        onMouseDown={(event) => event.stopPropagation()}
                        onPointerDown={(event) => event.stopPropagation()}
                    >
                        {suggestions.length > 0 || phase === "loading" ? (
                            <MentionSuggestionPopup localizer={localizer}
                                items={suggestions}
                                selectedIndex={selectedSuggestionIndex}
                                onHover={(index) => setSelectedSuggestionIndex(index)}
                                onSelect={(index) => {
                                    const suggestion = suggestions[index];
                                    if (!suggestion) {
                                        return;
                                    }
                                    handleSelectSuggestion(suggestion);
                                }}
                                highlightQuery={normalizedQuery}
                                isLoading={phase === "loading"}
                            />
                        ) : statusMessage ? (
                            <div className="overflow-hidden rounded-[1.25rem] border border-border/70 bg-[color-mix(in_srgb,var(--background1)_92%,white)] p-3 text-sm text-muted-foreground shadow-[var(--surface-shadow-lg)]">
                                {statusMessage}
                            </div>
                        ) : null}
                    </div>,
                    document.body
                )
                : null}
        </div>
    );
});

export { UsernameSuggestionField };
